import { useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BuildOutlined,
  CalendarMonthOutlined,
  DeleteOutlined,
  EventBusyOutlined,
  HealingOutlined,
  PauseOutlined,
  PlayArrowOutlined,
  ReplayOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import {
  MediaAgentLifecycleConflictError,
  useCancelScheduledMediaAgentMaintenanceMutation,
  useMediaAgentLifecycleMutation,
  useScheduleMediaAgentMaintenanceMutation,
  type CapacityPreflightBlocker,
} from '@/state/mutations/media-agent-admin/use-media-agent-lifecycle-mutations';
import {
  useRecoverMediaAgentMutation,
  useReissueMediaAgentTokenMutation,
} from '@/state/mutations/media-agent-admin/use-media-agent-recovery-mutations';
import { useForceReleaseMediaAgentMutation } from '@/state/mutations/media-agent-admin/use-pair-media-agent-device';
import { hasMediaAgentAuthIssue, isMediaAgentHealStuck } from '@/helpers/media-agent-automation.helper';
import type { MediaAgentPoolMemberDto } from '@/types/dtos/media-agent/media-agent-pool.dto';
import { VmFontPackIndicator } from '@/components/common/vm-font-pack-indicator';

export function MediaAgentVmCell({ agent }: { agent: MediaAgentPoolMemberDto }) {
  if (!agent.vms?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <>
      {agent.vms.map((vm) => (
        <Stack key={vm.vmId} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {vm.vmId}
          </Typography>
          <VmFontPackIndicator fontPack={vm.fontPack} />
          <Chip size="small" label={vm.vmStatus} variant="outlined" />
        </Stack>
      ))}
    </>
  );
}

type ConfirmAction =
  | 'maintenance'
  | 'pause'
  | 'forceRelease'
  | 'delete'
  | 'schedule'
  | 'cancelSchedule'
  | 'reissueToken'
  | 'recover';

function formatBlockers(blockers: CapacityPreflightBlocker[]): string {
  return blockers
    .map((blocker) => {
      if (blocker.code === 'capacity_shortfall' && blocker.details) {
        const at = typeof blocker.details.at === 'string' ? blocker.details.at : '';
        const required = blocker.details.required;
        const capacity = blocker.details.capacity;
        return `${blocker.message}${at ? ` (${at}: need ${required}, have ${capacity})` : ''}`;
      }
      if (blocker.code === 'assigned_booking' && blocker.details?.bookingId) {
        return `${blocker.message} (${String(blocker.details.bookingId)})`;
      }
      return blocker.message;
    })
    .join(' ');
}

function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function MediaAgentLifecycleActions({ agent }: { agent: MediaAgentPoolMemberDto }) {
  const t = useTranslations();
  const notification = useNotifications();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [forceMode, setForceMode] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [blockers, setBlockers] = useState<CapacityPreflightBlocker[]>([]);
  const [scheduleStartsAt, setScheduleStartsAt] = useState('');
  const [scheduleEndsAt, setScheduleEndsAt] = useState('');

  const { mutateAsync: enterMaintenance, isPending: isMaintenance } = useMediaAgentLifecycleMutation('maintenance');
  const { mutateAsync: pause, isPending: isPausing } = useMediaAgentLifecycleMutation('pause');
  const { mutateAsync: resume, isPending: isResuming } = useMediaAgentLifecycleMutation('resume');
  const { mutateAsync: deleteAgent, isPending: isDeleting } = useMediaAgentLifecycleMutation('delete');
  const { mutateAsync: scheduleMaintenance, isPending: isScheduling } = useScheduleMediaAgentMaintenanceMutation();
  const { mutateAsync: cancelSchedule, isPending: isCancellingSchedule } =
    useCancelScheduledMediaAgentMaintenanceMutation();
  const { mutateAsync: forceRelease, isPending: isReleasing } = useForceReleaseMediaAgentMutation();
  const { mutateAsync: reissueToken, isPending: isReissuing } = useReissueMediaAgentTokenMutation();
  const { mutateAsync: recoverAgent, isPending: isRecovering } = useRecoverMediaAgentMutation();

  const busy =
    isMaintenance ||
    isPausing ||
    isResuming ||
    isDeleting ||
    isReleasing ||
    isScheduling ||
    isCancellingSchedule ||
    isReissuing ||
    isRecovering;
  const isAllocated = Boolean(agent.allocatedFlowId);
  const opsStatus = agent.opsStatus ?? 'active';
  const isAzure = agent.deployment === 'azure';
  const scheduled = agent.scheduledMaintenance;
  const authIssue = hasMediaAgentAuthIssue(agent);
  const healStuck = isMediaAgentHealStuck(agent);

  const enterMaintenanceLabel = t('admin.mediaAgentAdmin.enterMaintenance', undefined, 'Enter maintenance');
  const pauseLabel = t('admin.mediaAgentAdmin.pause', undefined, 'Pause');
  const resumeLabel = t('admin.mediaAgentAdmin.resume', undefined, 'Resume');
  const forceReleaseLabel = t('admin.mediaAgentAdmin.forceRelease', undefined, 'Force release allocation');
  const deleteLabel = t('admin.mediaAgentAdmin.deleteHint', undefined, 'Delete agent');
  const scheduleLabel = t('admin.mediaAgentAdmin.scheduleMaintenance', undefined, 'Schedule maintenance');
  const cancelScheduleLabel = t(
    'admin.mediaAgentAdmin.cancelScheduledMaintenance',
    undefined,
    'Cancel scheduled maintenance',
  );
  const reissueTokenLabel = t('admin.mediaAgentAdmin.reissueToken', undefined, 'Reissue token');
  const recoverLabel = t('admin.mediaAgentAdmin.recover', undefined, 'Recover');

  const closeConfirm = () => {
    setConfirmAction(null);
    setForceMode(false);
    setForceReason('');
    setBlockers([]);
  };

  const openConfirm = (action: ConfirmAction) => {
    setForceMode(false);
    setForceReason('');
    setBlockers([]);
    if (action === 'schedule') {
      const start = new Date(Date.now() + 60 * 60 * 1000);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      setScheduleStartsAt(toLocalInputValue(start));
      setScheduleEndsAt(toLocalInputValue(end));
    }
    setConfirmAction(action);
  };

  const handleCapacityActionError = (err: unknown, fallback: string) => {
    if (err instanceof MediaAgentLifecycleConflictError) {
      setBlockers(err.blockers);
      setForceMode(true);
      notification.notifyError(
        t(
          'admin.mediaAgentAdmin.capacityBlocked',
          undefined,
          'Blocked by capacity preflight — add a reason to force, or cancel.',
        ),
      );
      return;
    }
    notification.notifyError(fallback);
  };

  const onEnterMaintenance = async () => {
    try {
      await enterMaintenance({
        playerId: agent.playerId,
        body: forceMode ? { force: true, reason: forceReason.trim() } : {},
      });
      notification.notifySuccess(t('admin.mediaAgentAdmin.maintenanceEnabled', undefined, 'Maintenance mode enabled'));
      closeConfirm();
    } catch (err) {
      handleCapacityActionError(
        err,
        t('admin.mediaAgentAdmin.maintenanceError', undefined, 'Failed to update maintenance mode'),
      );
    }
  };

  const onPause = async () => {
    try {
      await pause({
        playerId: agent.playerId,
        body: forceMode ? { force: true, reason: forceReason.trim() } : {},
      });
      notification.notifySuccess(t('admin.mediaAgentAdmin.pauseSuccess', undefined, 'Agent paused'));
      closeConfirm();
    } catch (err) {
      handleCapacityActionError(err, t('admin.mediaAgentAdmin.pauseError', undefined, 'Failed to pause agent'));
    }
  };

  const onSchedule = async () => {
    if (!scheduleStartsAt || !scheduleEndsAt) {
      notification.notifyError(
        t('admin.mediaAgentAdmin.scheduleRequired', undefined, 'Start and end times are required'),
      );
      return;
    }
    const startsAt = new Date(scheduleStartsAt);
    const endsAt = new Date(scheduleEndsAt);
    if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
      notification.notifyError(
        t('admin.mediaAgentAdmin.scheduleInvalidDates', undefined, 'Start and end times must be valid dates'),
      );
      return;
    }
    if (startsAt.getTime() >= endsAt.getTime()) {
      notification.notifyError(
        t('admin.mediaAgentAdmin.scheduleEndBeforeStart', undefined, 'End time must be after start time'),
      );
      return;
    }
    try {
      await scheduleMaintenance({
        playerId: agent.playerId,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        force: forceMode || undefined,
        reason: forceMode ? forceReason.trim() : forceReason.trim() || undefined,
      });
      notification.notifySuccess(t('admin.mediaAgentAdmin.scheduleSuccess', undefined, 'Maintenance scheduled'));
      closeConfirm();
    } catch (err) {
      handleCapacityActionError(
        err,
        t('admin.mediaAgentAdmin.scheduleError', undefined, 'Failed to schedule maintenance'),
      );
    }
  };

  const onCancelSchedule = async () => {
    try {
      await cancelSchedule(agent.playerId);
      notification.notifySuccess(
        t('admin.mediaAgentAdmin.cancelScheduleSuccess', undefined, 'Scheduled maintenance cancelled'),
      );
      closeConfirm();
    } catch {
      notification.notifyError(
        t('admin.mediaAgentAdmin.cancelScheduleError', undefined, 'Failed to cancel scheduled maintenance'),
      );
    }
  };

  const onForceRelease = async () => {
    try {
      await forceRelease(agent.playerId);
      notification.notifySuccess(t('admin.mediaAgentAdmin.forceReleaseSuccess', undefined, 'Allocation released'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.forceReleaseError', undefined, 'Failed to force release'));
    }
  };

  const onReissueToken = async () => {
    try {
      await reissueToken(agent.playerId);
      notification.notifySuccess(
        t('admin.mediaAgentAdmin.reissueTokenSuccess', undefined, 'Fresh token re-injected — shell is reconnecting'),
      );
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.reissueTokenError', undefined, 'Failed to reissue token'));
    }
  };

  const onRecover = async () => {
    try {
      await recoverAgent({ playerId: agent.playerId, mode: 'auto' });
      notification.notifySuccess(t('admin.mediaAgentAdmin.recoverSuccess', undefined, 'Recovery started'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.recoverError', undefined, 'Failed to start recovery'));
    }
  };

  const onDelete = async () => {
    try {
      await deleteAgent(agent.playerId);
      notification.notifySuccess(t('admin.mediaAgentAdmin.deleteSuccess', undefined, 'Agent deleted'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.deleteError', undefined, 'Delete failed'));
    }
  };

  const confirmPending = () => {
    switch (confirmAction) {
      case 'maintenance':
        return void onEnterMaintenance();
      case 'pause':
        return void onPause();
      case 'schedule':
        return void onSchedule();
      case 'cancelSchedule':
        return void onCancelSchedule();
      case 'forceRelease':
        return void onForceRelease();
      case 'reissueToken':
        return void onReissueToken();
      case 'recover':
        return void onRecover();
      case 'delete':
        return void onDelete();
      default:
        return;
    }
  };

  const confirmTitle = useMemo(() => {
    switch (confirmAction) {
      case 'maintenance':
        return t('admin.mediaAgentAdmin.maintenanceConfirmTitle', undefined, 'Enter maintenance?');
      case 'pause':
        return t('admin.mediaAgentAdmin.pauseConfirmTitle', undefined, 'Pause media agent?');
      case 'schedule':
        return t('admin.mediaAgentAdmin.scheduleConfirmTitle', undefined, 'Schedule maintenance?');
      case 'cancelSchedule':
        return t('admin.mediaAgentAdmin.cancelScheduleConfirmTitle', undefined, 'Cancel scheduled maintenance?');
      case 'forceRelease':
        return t('admin.mediaAgentAdmin.forceReleaseConfirmTitle', undefined, 'Force release allocation?');
      case 'reissueToken':
        return t('admin.mediaAgentAdmin.reissueTokenConfirmTitle', undefined, 'Reissue player token?');
      case 'recover':
        return t('admin.mediaAgentAdmin.recoverConfirmTitle', undefined, 'Recover media agent?');
      case 'delete':
        return t('admin.mediaAgentAdmin.deleteConfirmTitle', undefined, 'Delete media agent?');
      default:
        return '';
    }
  }, [confirmAction, t]);

  const confirmText = useMemo(() => {
    switch (confirmAction) {
      case 'maintenance':
        return t(
          'admin.mediaAgentAdmin.maintenanceConfirmText',
          undefined,
          'Put this media agent into maintenance? It will stop accepting new bookings until resumed.',
        );
      case 'pause':
        return t(
          'admin.mediaAgentAdmin.pauseConfirmText',
          undefined,
          'Pause this media agent? The Azure VM will be stopped and removed from the pool until resumed.',
        );
      case 'schedule':
        return t(
          'admin.mediaAgentAdmin.scheduleConfirmText',
          undefined,
          'Schedule a maintenance window. The agent leaves the pool at start and returns at end.',
        );
      case 'cancelSchedule':
        return t(
          'admin.mediaAgentAdmin.cancelScheduleConfirmText',
          undefined,
          'Cancel the upcoming scheduled maintenance window?',
        );
      case 'forceRelease':
        return t(
          'admin.mediaAgentAdmin.forceReleaseConfirmText',
          undefined,
          'Force-release the current allocation? Any live media-agent session may be interrupted.',
        );
      case 'reissueToken':
        return t(
          'admin.mediaAgentAdmin.reissueTokenConfirmText',
          undefined,
          'Re-inject a fresh player token onto the running Azure VM. Use when the shell is up but stuck on a rejected token.',
        );
      case 'recover':
        return t(
          'admin.mediaAgentAdmin.recoverConfirmText',
          undefined,
          'Run automatic recovery: stop streams and close hung apps, escalating to a process restart if needed. A live session may be interrupted or moved to a spare shell.',
        );
      case 'delete':
        return t('admin.mediaAgentAdmin.deleteConfirmText', undefined, 'This removes the pool shell and its VM.');
      default:
        return '';
    }
  }, [confirmAction, t]);

  const confirmLabel = (() => {
    switch (confirmAction) {
      case 'maintenance':
        return forceMode
          ? t('admin.mediaAgentAdmin.forceMaintenance', undefined, 'Force maintenance')
          : enterMaintenanceLabel;
      case 'pause':
        return forceMode ? t('admin.mediaAgentAdmin.forcePause', undefined, 'Force pause') : pauseLabel;
      case 'schedule':
        return forceMode ? t('admin.mediaAgentAdmin.forceSchedule', undefined, 'Force schedule') : scheduleLabel;
      case 'cancelSchedule':
        return cancelScheduleLabel;
      case 'forceRelease':
        return forceReleaseLabel;
      case 'reissueToken':
        return reissueTokenLabel;
      case 'recover':
        return recoverLabel;
      default:
        return t('admin.mediaAgentAdmin.delete', undefined, 'Delete');
    }
  })();

  const confirmPendingBusy =
    (confirmAction === 'maintenance' && isMaintenance) ||
    (confirmAction === 'pause' && isPausing) ||
    (confirmAction === 'schedule' && isScheduling) ||
    (confirmAction === 'cancelSchedule' && isCancellingSchedule) ||
    (confirmAction === 'forceRelease' && isReleasing) ||
    (confirmAction === 'reissueToken' && isReissuing) ||
    (confirmAction === 'recover' && isRecovering) ||
    (confirmAction === 'delete' && isDeleting);

  const confirmIsDestructive =
    confirmAction === 'delete' ||
    confirmAction === 'forceRelease' ||
    confirmAction === 'pause' ||
    confirmAction === 'recover' ||
    forceMode;

  const forceReasonRequired =
    forceMode && (confirmAction === 'maintenance' || confirmAction === 'pause' || confirmAction === 'schedule');
  const confirmDisabled = confirmPendingBusy || (forceReasonRequired && forceReason.trim().length < 3);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {opsStatus === 'active' && !isAllocated && (
        <Tooltip title={enterMaintenanceLabel}>
          <span>
            <IconButton
              size="small"
              aria-label={enterMaintenanceLabel}
              disabled={busy}
              onClick={() => openConfirm('maintenance')}
            >
              {isMaintenance ? <CircularProgress size={16} /> : <BuildOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {opsStatus === 'active' && !isAllocated && !scheduled && (
        <Tooltip title={scheduleLabel}>
          <span>
            <IconButton size="small" aria-label={scheduleLabel} disabled={busy} onClick={() => openConfirm('schedule')}>
              {isScheduling ? <CircularProgress size={16} /> : <CalendarMonthOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {scheduled?.status === 'scheduled' && (
        <Tooltip title={cancelScheduleLabel}>
          <span>
            <IconButton
              size="small"
              aria-label={cancelScheduleLabel}
              disabled={busy}
              onClick={() => openConfirm('cancelSchedule')}
            >
              {isCancellingSchedule ? <CircularProgress size={16} /> : <EventBusyOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {isAzure && opsStatus === 'active' && !isAllocated && (
        <Tooltip title={pauseLabel}>
          <span>
            <IconButton size="small" aria-label={pauseLabel} disabled={busy} onClick={() => openConfirm('pause')}>
              {isPausing ? <CircularProgress size={16} /> : <PauseOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {(opsStatus === 'maintenance' || opsStatus === 'paused') && (
        <Tooltip title={resumeLabel}>
          <span>
            <IconButton
              size="small"
              aria-label={resumeLabel}
              disabled={busy}
              onClick={() => void resume(agent.playerId)}
            >
              {isResuming ? <CircularProgress size={16} /> : <PlayArrowOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {isAllocated && (
        <Tooltip title={forceReleaseLabel}>
          <span>
            <IconButton
              size="small"
              aria-label={forceReleaseLabel}
              disabled={busy}
              onClick={() => openConfirm('forceRelease')}
            >
              {isReleasing ? <CircularProgress size={16} /> : <ReplayOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {isAzure && (
        <Tooltip
          title={
            authIssue
              ? t(
                  'admin.mediaAgentAdmin.reissueTokenStaleHint',
                  undefined,
                  'Reissue token — shell is stuck on a rejected token',
                )
              : reissueTokenLabel
          }
        >
          <span>
            <IconButton
              size="small"
              aria-label={reissueTokenLabel}
              color={authIssue ? 'warning' : 'default'}
              disabled={busy}
              onClick={() => openConfirm('reissueToken')}
            >
              {isReissuing ? <CircularProgress size={16} /> : <VpnKeyOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip
        title={
          healStuck
            ? t('admin.mediaAgentAdmin.recoverStuckHint', undefined, 'Recover — automatic heal is stuck')
            : recoverLabel
        }
      >
        <span>
          <IconButton
            size="small"
            aria-label={recoverLabel}
            color={healStuck ? 'warning' : 'default'}
            disabled={busy}
            onClick={() => openConfirm('recover')}
          >
            {isRecovering ? <CircularProgress size={16} /> : <HealingOutlined fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      {!isAllocated && (
        <Tooltip title={deleteLabel}>
          <span>
            <IconButton size="small" aria-label={deleteLabel} disabled={busy} onClick={() => openConfirm('delete')}>
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Dialog open={confirmAction !== null} onClose={closeConfirm} fullWidth maxWidth="sm">
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmText}</DialogContentText>
          {confirmAction === 'schedule' && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label={t('admin.mediaAgentAdmin.scheduleStartsAt', undefined, 'Starts at')}
                type="datetime-local"
                value={scheduleStartsAt}
                onChange={(e) => setScheduleStartsAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={t('admin.mediaAgentAdmin.scheduleEndsAt', undefined, 'Ends at')}
                type="datetime-local"
                value={scheduleEndsAt}
                onChange={(e) => setScheduleEndsAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          )}
          {blockers.length > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {formatBlockers(blockers)}
            </Typography>
          )}
          {forceReasonRequired && (
            <TextField
              sx={{ mt: 2 }}
              label={t('admin.mediaAgentAdmin.forceReason', undefined, 'Force reason')}
              value={forceReason}
              onChange={(e) => setForceReason(e.target.value)}
              helperText={t(
                'admin.mediaAgentAdmin.forceReasonHelp',
                undefined,
                'Required to override capacity/booking blockers (min 3 characters).',
              )}
              fullWidth
              multiline
              minRows={2}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>{t('common.cancel', undefined, 'Cancel')}</Button>
          <Button
            variant="contained"
            color={confirmIsDestructive ? 'error' : 'warning'}
            onClick={confirmPending}
            disabled={confirmDisabled}
          >
            {confirmPendingBusy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
